import * as THREE from "three";
import type { PanoramaSource, PanoramaNode } from "./PanoramaSource.ts";
import type { HousePanoramaEntry } from "../types.ts";

const CACHE_LIMIT = 64; // hold all panoramas in memory for instant navigation

/**
 * PanoramaSource backed by HousePanoramaEntry[] — loads equirect textures
 * via a blob-URL callback, computes N nearest neighbours by distance.
 */
export class HouseEquirectSource implements PanoramaSource {
  private readonly nodes_: PanoramaNode[] = [];
  private readonly adjacency = new Map<number, number[]>();
  private readonly urlCache = new Map<number, string>();

  private readonly material: THREE.MeshBasicMaterial;
  private readonly texCache = new Map<number, THREE.Texture>();
  private texOrder: number[] = [];
  private readonly texLoader = new THREE.TextureLoader();

  constructor(
    private readonly entries: HousePanoramaEntry[],
    private readonly getBlobUrl: (captureId: string, filename: string) => Promise<string | null>,
    neighborCount = 3,
  ) {
    this.material = new THREE.MeshBasicMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      color: 0x111111,
    });

    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      this.nodes_.push({
        id: i,
        position: new THREE.Vector3(e.x, e.y, e.z),
        initialYaw: 0,
        initialPitch: 0,
      });
    }

    for (let i = 0; i < entries.length; i++) {
      const me = this.nodes_[i].position;
      const nearest = this.nodes_
        .filter(n => n.id !== i)
        .sort((a, b) => a.position.distanceToSquared(me) - b.position.distanceToSquared(me))
        .slice(0, neighborCount)
        .map(n => n.id);
      this.adjacency.set(i, nearest);
    }
  }

  getNodes(): PanoramaNode[] { return this.nodes_; }
  getNeighbors(nodeId: number): number[] { return this.adjacency.get(nodeId) ?? []; }
  getMaterial(): THREE.Material { return this.material; }
  getSphereYaw(nodeId: number): number {
    const e = this.entries[nodeId];
    return e ? (e.yaw * Math.PI) / 180 + Math.PI / 2 : Math.PI / 2;
  }

  async loadNode(nodeId: number): Promise<void> {
    const tex = await this._getOrLoad(nodeId);
    if (!tex) return;
    this.material.map = tex;
    this.material.color.setHex(0xffffff);
    this.material.needsUpdate = true;
  }

  async preloadNode(nodeId: number): Promise<void> {
    await this._getOrLoad(nodeId);
  }

  preloadNeighbors(nodeId: number): void {
    for (const nid of this.adjacency.get(nodeId) ?? []) {
      if (!this.texCache.has(nid)) void this._getOrLoad(nid);
    }
  }

  dispose(): void {
    this.material.dispose();
    for (const t of this.texCache.values()) t.dispose();
    this.texCache.clear();
    this.texOrder = [];
  }

  private async _getOrLoad(nodeId: number): Promise<THREE.Texture | null> {
    const cached = this.texCache.get(nodeId);
    if (cached) { this._touchLru(nodeId); return cached; }

    const entry = this.entries[nodeId];
    if (!entry) return null;

    let url = this.urlCache.get(nodeId);
    if (!url) {
      const resolved = await this.getBlobUrl(entry.capture_id, entry.filename);
      if (!resolved) {
        console.error(`[HouseEquirectSource] getBlobUrl returned null for node ${nodeId}`, entry);
        return null;
      }
      url = resolved;
      this.urlCache.set(nodeId, url);
    }

    try {
      const tex = await this.texLoader.loadAsync(url);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.mapping = THREE.EquirectangularReflectionMapping;
      tex.wrapS = THREE.RepeatWrapping;
      tex.repeat.x = -1;
      tex.offset.x = 1;
      this.texCache.set(nodeId, tex);
      this.texOrder.push(nodeId);
      this._evictIfNeeded();
      return tex;
    } catch (err) {
      console.error(`[HouseEquirectSource] failed to load texture for node ${nodeId}`, err, url);
      return null;
    }
  }

  private _touchLru(nodeId: number): void {
    const i = this.texOrder.indexOf(nodeId);
    if (i >= 0) this.texOrder.splice(i, 1);
    this.texOrder.push(nodeId);
  }

  private _evictIfNeeded(): void {
    while (this.texOrder.length > CACHE_LIMIT) {
      const evict = this.texOrder.shift()!;
      const tex = this.texCache.get(evict);
      if (tex && this.material.map === tex) {
        this.texOrder.push(evict);
        if (this.texOrder.length > CACHE_LIMIT * 2) break;
        continue;
      }
      if (tex) tex.dispose();
      this.texCache.delete(evict);
    }
  }
}
